import type {
  CatalogProvider,
  IntegrationSurface,
  ConnectionStatus,
  ConnectionErrorCode,
  LibraryCollectionTarget,
} from "../../api/integrations";
import { needsReconnect } from "../../pages/profile-settings/integrations-hub/connection-health";

export const INTEGRATIONS_PATH = "/dashboard/profile/integrations";
export const capabilityKey = (surface: IntegrationSurface) =>
  `${surface}:${surface === "inbox" ? "destination" : "source"}`;
export const supports = (
  provider: CatalogProvider,
  surface: IntegrationSurface
) =>
  provider.capabilities.some(
    (cap) =>
      cap.surface === surface &&
      cap.direction === (surface === "inbox" ? "destination" : "source")
  );

// Registry support is not evidence that this capability was configured. Configs
// survive disconnects, so a former connection never becomes a discovery prompt.
export function configuredProviders(
  providers: CatalogProvider[],
  surface?: IntegrationSurface
): CatalogProvider[] {
  return providers.flatMap((provider) => {
    const connection = provider.connection;
    if (!connection) return [];
    const configured = connection.configuredCapabilities ?? [];
    const selected = surface
      ? configured.filter((cap) => cap.capability === capabilityKey(surface))
      : configured;
    if (selected.length === 0) return [];
    if (!surface) return [provider];
    const cap = selected[0];
    return [
      {
        ...provider,
        connection: {
          ...connection,
          status: ["error", "disconnected", "paused"].includes(
            connection.status
          )
            ? connection.status
            : cap.enabled
              ? cap.activity
                ? "syncing"
                : "active"
              : "paused",
          configuredCapabilities: selected,
          lastRun: cap.lastRun,
          activity: cap.activity,
          syncedTotals: cap.syncedTotals,
        },
      },
    ];
  });
}

export function unconfiguredProviders(
  providers: CatalogProvider[],
  surface?: IntegrationSurface
): CatalogProvider[] {
  return providers.filter(
    (provider) =>
      provider.connection &&
      (["pending_config", "error", "disconnected", "paused"].includes(
        provider.connection.status
      ) ||
        !provider.connection.configuredCapabilities?.length) &&
      (!surface || supports(provider, surface)) &&
      configuredProviders([provider], surface).length === 0
  );
}

export function discoveryCandidates(
  providers: CatalogProvider[],
  surface?: IntegrationSurface
): CatalogProvider[] {
  return providers.filter(
    (provider) =>
      !provider.comingSoon &&
      !provider.requiresUpgrade &&
      (!provider.connection ||
        provider.connection.configuredCapabilities !== undefined) &&
      (!surface || supports(provider, surface)) &&
      configuredProviders([provider], surface).length === 0 &&
      unconfiguredProviders([provider], surface).length === 0
  );
}

export function connectionHealth(provider: CatalogProvider): {
  status: ConnectionStatus;
  errorCode?: ConnectionErrorCode;
} {
  const connection = provider.connection;
  if (!connection) return { status: "disconnected" };
  if (
    ["error", "disconnected", "paused", "pending_config"].includes(
      connection.status
    )
  )
    return { status: connection.status, errorCode: connection.error?.code };
  if (
    connection.status === "syncing" ||
    ["queued", "running"].includes(connection.activity?.phase ?? "")
  )
    return { status: "syncing" };
  if (connection.lastRun?.ok === false)
    return { status: "error", errorCode: "sync_failing" };
  return { status: connection.status };
}

export function connectionLabel(
  provider: CatalogProvider,
  now = Date.now()
): string {
  const connection = provider.connection;
  if (!connection) return "Not connected";
  if (needsReconnect(connection)) return "Reconnect needed";
  const health = connectionHealth(provider);
  if (health.status === "disconnected") return "Disconnected";
  if (health.status === "error") return "Sync needs attention";
  if (health.status === "paused") return "Paused";
  if (health.status === "pending_config") return "Needs setup";
  if (
    connection.status === "syncing" ||
    ["queued", "running"].includes(connection.activity?.phase ?? "")
  )
    return "Syncing…";
  return relativeSyncLabel(connection.lastRun?.at, now) ?? "Connected";
}

export function relativeSyncLabel(
  timestamp?: string,
  now = Date.now()
): string | undefined {
  const at = timestamp ? Date.parse(timestamp) : Number.NaN;
  if (!Number.isFinite(at)) return undefined;
  const minutes = Math.max(0, Math.floor((now - at) / 60_000));
  return `Synced ${minutes < 1 ? "just now" : minutes < 60 ? `${minutes}m ago` : minutes < 1440 ? `${Math.floor(minutes / 60)}h ago` : `${Math.floor(minutes / 1440)}d ago`}`;
}

export function statusPriority(provider: CatalogProvider): number {
  const { status } = connectionHealth(provider);
  return status === "error" || status === "disconnected"
    ? 0
    : status === "pending_config"
      ? 1
      : status === "paused"
        ? 2
        : status === "syncing" || provider.connection?.activity
          ? 3
          : 4;
}

export function primaryProvider(
  providers: CatalogProvider[]
): CatalogProvider | undefined {
  return providers.toSorted(
    (a, b) =>
      statusPriority(a) - statusPriority(b) ||
      Date.parse(a.connection?.lastRun?.at ?? "") -
        Date.parse(b.connection?.lastRun?.at ?? "")
  )[0];
}

const atMs = (at?: string) => {
  const ms = at ? Date.parse(at) : Number.NaN;
  return Number.isFinite(ms) ? ms : 0;
};

// The folders of one provider's library capability. The catalog lists one entry per
// folder (SKE-3963), so a collection fed by two folders has two entries.
export function libraryTargetsOf(
  provider: CatalogProvider
): LibraryCollectionTarget[] {
  return (
    provider.connection?.configuredCapabilities?.find(
      (cap) => cap.capability === "library:source"
    )?.collectionTargets ?? []
  );
}

// Every folder that feeds `collectionId` from one provider's library capability.
export function collectionTargetsFor(
  provider: CatalogProvider,
  collectionId: string
): LibraryCollectionTarget[] {
  return libraryTargetsOf(provider).filter(
    (item) => item.collectionId === collectionId
  );
}

// True when every folder feeding the collection from this provider was removed, so
// the collection keeps its files but nothing syncs into it.
export function feedStopped(provider: CatalogProvider): boolean {
  const targets = libraryTargetsOf(provider);
  return (
    targets.length > 0 && targets.every((target) => target.status === "stopped")
  );
}

// The providers feeding one collection, each narrowed to that collection's folders.
// Health merges every feeding folder: the newest in-flight run, the newest last run,
// and a failure (or a folder paused on setup) on any folder still wins over a newer
// success elsewhere. Removed (`stopped`) folders keep the provider listed but do not
// count toward its health.
export function collectionProviders(
  providers: CatalogProvider[],
  collectionId: string
): CatalogProvider[] {
  return providers.flatMap((provider) => {
    const connection = provider.connection;
    const capability = connection?.configuredCapabilities?.find(
      (cap) => cap.capability === "library:source"
    );
    const targets = collectionTargetsFor(provider, collectionId);
    if (!connection || !capability || targets.length === 0) return [];
    const live = targets.filter((target) => target.status !== "stopped");
    const activity = live
      .flatMap((target) => (target.activity ? [target.activity] : []))
      .toSorted((a, b) => atMs(b.since) - atMs(a.since))[0];
    const runs = live.flatMap((target) =>
      target.lastRun ? [target.lastRun] : []
    );
    const newestRun = runs.toSorted((a, b) => atMs(b.at) - atMs(a.at))[0];
    const failing =
      runs.some((run) => run.ok === false) ||
      live.some((target) => target.status === "needs_setup");
    const lastRun =
      newestRun || failing
        ? { ...newestRun, ...(failing ? { ok: false } : {}) }
        : undefined;
    const retainConnectionState =
      ["disconnected", "paused", "pending_config"].includes(
        connection.status
      ) || needsReconnect(connection);
    return [
      {
        ...provider,
        connection: {
          ...connection,
          status: retainConnectionState
            ? connection.status
            : capability.enabled
              ? activity
                ? "syncing"
                : "active"
              : "paused",
          error: retainConnectionState ? connection.error : undefined,
          configuredCapabilities: [
            { ...capability, collectionTargets: targets },
          ],
          lastRun,
          activity,
          syncedTotals: undefined,
        },
      },
    ];
  });
}
